import { ActionIcon, Badge, Group, Paper, Table, Text, Menu } from '@mantine/core';
import { IconEdit, IconTrash, IconDots } from '@tabler/icons-react';

interface Workout {
	id: string;
	name: string;
	category: {
		id: string;
		name: string;
	};
	intensity: string;
	frequency: number;
	frequencyPeriod: string;
	exercises: any[];
	isActive: boolean;
}

interface WorkoutsListProps {
	workouts: Workout[];
	onEdit: (workout: Workout) => void;
	onDelete: (workoutId: string) => void;
	loading?: boolean;
}

const INTENSITY_COLORS: Record<string, string> = {
	CASUAL: 'blue',
	MODERATE: 'yellow',
	INTENSIVE: 'red',
};

const FREQUENCY_PERIOD_LABELS: Record<string, string> = {
	DAILY: 'day',
	WEEKLY: 'week',
	MONTHLY: 'month',
	YEARLY: 'year',
};

export function WorkoutsList({ workouts, onEdit, onDelete, loading }: WorkoutsListProps) {
	if (workouts.length === 0) {
		return (
			<Text c="dimmed" ta="center" py="xl">
				No workouts found. Create your first workout to get started.
			</Text>
		);
	}

	return (
		<Paper withBorder p="lg" style={{ overflow: 'hidden' }}>
			<Table.ScrollContainer minWidth={800}>
				<Table>
					<Table.Thead>
						<Table.Tr>
							<Table.Th>Name</Table.Th>
							<Table.Th>Category</Table.Th>
							<Table.Th>Intensity</Table.Th>
							<Table.Th>Frequency</Table.Th>
							<Table.Th>Exercises</Table.Th>
							<Table.Th>Status</Table.Th>
							<Table.Th>Actions</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{workouts.map((workout) => (
							<Table.Tr key={workout.id}>
								<Table.Td>
									<Text fw={500}>{workout.name}</Text>
								</Table.Td>
								<Table.Td>
									<Text size="sm">{workout.category.name}</Text>
								</Table.Td>
								<Table.Td>
									<Badge color={INTENSITY_COLORS[workout.intensity]} variant="light">
										{workout.intensity}
									</Badge>
								</Table.Td>
								<Table.Td>
									<Text size="sm">
										{workout.frequency}x/{FREQUENCY_PERIOD_LABELS[workout.frequencyPeriod] || 'week'}
									</Text>
								</Table.Td>
								<Table.Td>
									<Text size="sm">{workout.exercises.length} exercises</Text>
								</Table.Td>
								<Table.Td>
									<Badge color={workout.isActive ? 'green' : 'gray'} variant="light">
										{workout.isActive ? 'Active' : 'Inactive'}
									</Badge>
								</Table.Td>
								<Table.Td>
									<Menu position="bottom-end" withinPortal>
										<Menu.Target>
											<ActionIcon variant="subtle" color="gray" disabled={loading}>
												<IconDots size={16} />
											</ActionIcon>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item
												leftSection={<IconEdit size={14} />}
												color="#1A4D66"
												onClick={() => onEdit(workout)}
											>
												Edit
											</Menu.Item>
											<Menu.Divider />
											<Menu.Item
												leftSection={<IconTrash size={14} />}
												color="red"
												onClick={() => onDelete(workout.id)}
											>
												Delete
											</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</Table.ScrollContainer>
		</Paper>
	);
}
